import { useEffect, useState } from "react";
import {
  Briefcase,
  Calendar,
  ExternalLink,
  Loader2,
  MessageSquare,
  Trash2,
  User,
  X,
} from "lucide-react";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import {
  useJiraIssueDetail,
  useJiraTransitions,
  useTransitionJiraIssue,
  useUnlinkJiraIssue,
  type JiraSupports,
} from "@/hooks/useJira";

interface Props {
  /** Planner project this drawer is opened from. Null when the drawer
   * is opened from a project-agnostic surface like Synnergie Workload.
   * When null: the 'Supports' banner and Unlink affordance are hidden;
   * status transitions still work (they don't depend on the planner
   * project). */
  projectId: string | null;
  issueKey: string | null;
  writeEnabled: boolean;
  /** Whether a planner link to the current parent actually exists for
   *  this issue — the caller knows (top-level row, not rolled up), the
   *  drawer can't tell from the issue alone. Gates Unlink.
   *
   *  Previously gated on `!issue.parent_key`, which conflated "child in
   *  Jira's hierarchy" with "not a planner link": a linked issue that
   *  happened to be a sub-task had NO unlink affordance anywhere, so a
   *  redundant link could never be removed (Brett, ETE-150 / SSE-734). */
  canUnlink?: boolean;
  onClose: () => void;
}

function statusPillClass(category: string | null | undefined): string {
  // Sitewide rule: gray (not in progress) / sky (in progress) / emerald (done).
  switch (category) {
    case "done":
      return "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30";
    case "indeterminate":
      return "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-500/30";
    case "new":
    default:
      return "bg-raised text-ink-3 ring-1 ring-line";
  }
}

function healthDot(health: string | null): string {
  switch (health) {
    case "green":
      return "bg-emerald-500";
    case "yellow":
      return "bg-amber-500";
    case "red":
      return "bg-rose-500";
    default:
      return "bg-muted-strong";
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Right-edge slide-over panel showing the full picture of one Jira
 * issue. Two audiences:
 *
 *   - Execs: scan the "Supports" banner (planner-side context — team,
 *     portfolio, sponsor, health, % complete, the planner project this
 *     card rolls up to) plus assignee/status/dates. Comments collapsed.
 *   - PM (Brett): expand comments to see the logistics conversation
 *     that explains what's actually happening.
 *
 * Description and comment HTML come pre-rendered from Jira (via
 * expand=renderedFields / renderedBody) and are sanitized with
 * DOMPurify before injection — Jira can emit arbitrary HTML for
 * mentions, links, code blocks, etc.
 */
export function JiraIssueDrawer({
  projectId,
  issueKey,
  writeEnabled,
  canUnlink = false,
  onClose,
}: Props) {
  const open = Boolean(issueKey);
  const detail = useJiraIssueDetail(projectId, issueKey);
  const transitions = useJiraTransitions(issueKey, open && writeEnabled);
  // The transition + unlink hooks need a project id for cache
  // invalidation. Empty string is a safe sentinel — invalidation just
  // misses the project-scoped query (which doesn't exist in this
  // surface). The drawer hides Unlink entirely when projectId is null.
  const transition = useTransitionJiraIssue(projectId ?? "");
  const unlink = useUnlinkJiraIssue(projectId ?? "");
  const [showComments, setShowComments] = useState(false);

  const handleTransition = async (transitionId: string) => {
    if (!issueKey) return;
    try {
      const result = await transition.mutateAsync({ issueKey, transitionId });
      toast.success(`${issueKey} → ${result.status ?? "transitioned"}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Transition failed");
    }
  };

  const handleUnlink = async () => {
    if (!issueKey) return;
    if (!confirm(`Unlink ${issueKey} from this project?`)) return;
    try {
      await unlink.mutateAsync(issueKey);
      toast.success(`Unlinked ${issueKey}`);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Unlink failed");
    }
  };

  // Reset comment expansion every time the user opens a different issue
  // — otherwise it carries over and surprises them.
  useEffect(() => {
    setShowComments(false);
  }, [issueKey]);

  // Esc to close — drawers should always be dismissible without mouse.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const d = detail.data;
  const issue = d?.issue;
  const supports = d?.supports ?? null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Scrim — click to dismiss */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-slate-900/30 backdrop-blur-sm"
      />
      {/* Panel */}
      <aside className="flex w-full max-w-2xl flex-col bg-surface shadow-2xl ring-1 ring-line">
        {/* Header */}
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-mono text-ink-muted">{issueKey}</span>
              {issue?.issue_type && (
                <span className="rounded bg-raised px-1.5 py-0.5 text-ink-3">
                  {issue.issue_type}
                </span>
              )}
              {issue?.priority && (
                <span className="rounded bg-raised px-1.5 py-0.5 text-ink-3">
                  {issue.priority}
                </span>
              )}
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 font-medium",
                  statusPillClass(issue?.status_category),
                )}
              >
                {issue?.status ?? "—"}
              </span>
            </div>
            <h2 className="mt-1.5 truncate text-base font-semibold text-ink">
              {issue?.summary ?? (detail.isLoading ? "Loading…" : "(no summary)")}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {issue?.url && (
              <a
                href={issue.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-xs text-ink-3 hover:bg-raised"
              >
                Open in Jira <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-ink-muted hover:bg-raised"
              aria-label="Close drawer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {detail.isLoading && (
            <div className="flex items-center justify-center py-16 text-sm text-ink-muted">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading issue…
            </div>
          )}
          {detail.isError && !d && (
            <div className="m-5 rounded-md border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300">
              Couldn't load this issue from Jira. The card may have been moved
              or deleted.
            </div>
          )}

          {d && (
            <div className="space-y-5 p-5">
              {supports && <SupportsBanner supports={supports} />}

              {/* People + dates */}
              <section className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Field icon={<User className="h-3.5 w-3.5" />} label="Assignee">
                  {issue?.assignee ?? <em className="text-ink-subtle">Unassigned</em>}
                </Field>
                <Field icon={<User className="h-3.5 w-3.5" />} label="Reporter">
                  {d.reporter ?? <em className="text-ink-subtle">—</em>}
                </Field>
                <Field icon={<Calendar className="h-3.5 w-3.5" />} label="Created">
                  {formatDate(d.created_at)}
                </Field>
                <Field icon={<Calendar className="h-3.5 w-3.5" />} label="Updated">
                  {formatDate(issue?.updated_at ?? null)}
                </Field>
                {/* Start comes from a Jira custom field auto-discovered
                    by the backend; the cached row carries it on every
                    list refresh. Falls back to em-dash when null. */}
                <Field icon={<Calendar className="h-3.5 w-3.5" />} label="Start">
                  {issue?.start_date ? formatDate(issue.start_date) : <em className="text-ink-subtle">—</em>}
                </Field>
                <Field icon={<Calendar className="h-3.5 w-3.5" />} label="Due">
                  {d.due_date ? formatDate(d.due_date) : <em className="text-ink-subtle">—</em>}
                </Field>
                {d.labels.length > 0 && (
                  <Field icon={<Briefcase className="h-3.5 w-3.5" />} label="Labels">
                    <span className="flex flex-wrap gap-1">
                      {d.labels.map((l) => (
                        <span
                          key={l}
                          className="rounded bg-raised px-1.5 py-0.5 text-xs text-ink-3"
                        >
                          {l}
                        </span>
                      ))}
                    </span>
                  </Field>
                )}
              </section>

              {/* Description */}
              {d.description_html ? (
                <section>
                  <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-muted">
                    Description
                  </h3>
                  <div
                    className="prose prose-sm max-w-none text-ink-2"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(d.description_html),
                    }}
                  />
                </section>
              ) : (
                <section className="text-sm italic text-ink-subtle">
                  No description.
                </section>
              )}

              {/* Comments */}
              <section>
                <button
                  onClick={() => setShowComments((v) => !v)}
                  className="flex w-full items-center justify-between rounded-md border border-line px-3 py-2 text-sm hover:bg-raised"
                >
                  <span className="inline-flex items-center gap-2 text-ink-2">
                    <MessageSquare className="h-4 w-4 text-ink-subtle" />
                    Comments
                    <span className="rounded-full bg-raised px-2 py-0.5 text-xs text-ink-3">
                      {d.comment_count}
                    </span>
                  </span>
                  <span className="text-xs text-ink-muted">
                    {showComments ? "Hide" : "Show"}
                  </span>
                </button>

                {showComments && (
                  <ul className="mt-3 space-y-3">
                    {d.comments.length === 0 && (
                      <li className="rounded-md border border-dashed border-line p-4 text-center text-sm text-ink-subtle">
                        No comments yet.
                      </li>
                    )}
                    {d.comments.map((c, i) => (
                      <li
                        key={`${c.created}-${i}`}
                        className="rounded-md border border-line bg-surface p-3"
                      >
                        <div className="mb-1 flex items-center justify-between text-xs text-ink-muted">
                          <span className="font-medium text-ink-2">
                            {c.author ?? "Unknown"}
                          </span>
                          <span>{formatDateTime(c.created)}</span>
                        </div>
                        <div
                          className="prose prose-sm max-w-none text-ink-2"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(c.body_html),
                          }}
                        />
                      </li>
                    ))}
                    {d.comment_count > d.comments.length && (
                      <li className="text-center text-xs text-ink-subtle">
                        Showing {d.comments.length} of {d.comment_count} —
                        older comments truncated. Open in Jira to see all.
                      </li>
                    )}
                  </ul>
                )}
              </section>
            </div>
          )}
        </div>

        {/* Action footer — only shown when there's something to put
            there (write mode for transitions, top-level for unlink). */}
        {d && (writeEnabled || (projectId && canUnlink)) && (
          <footer className="space-y-2 border-t border-line bg-canvas px-5 py-3">
            {writeEnabled && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-ink-muted">
                  Move to
                </span>
                {transitions.isLoading && (
                  <Loader2 className="h-3 w-3 animate-spin text-ink-subtle" />
                )}
                {transitions.data?.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => handleTransition(t.id)}
                    disabled={transition.isPending}
                    className="rounded-full border border-line bg-surface px-2.5 py-0.5 text-xs hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-700 dark:hover:text-brand-navy-300 disabled:opacity-50"
                  >
                    {t.name}
                  </button>
                ))}
                {transitions.isError && (
                  <span className="text-xs text-rose-500">
                    Couldn't load transitions.
                  </span>
                )}
              </div>
            )}
            {projectId && canUnlink && (
              <div className="flex justify-end">
                <button
                  onClick={handleUnlink}
                  disabled={unlink.isPending}
                  className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800 dark:hover:text-rose-300 disabled:opacity-50"
                >
                  <Trash2 className="h-3 w-3" /> Unlink from this project
                </button>
              </div>
            )}
          </footer>
        )}
      </aside>
    </div>
  );
}

function Field({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-0.5 flex items-center gap-1.5 text-xs uppercase tracking-wide text-ink-muted">
        {icon}
        {label}
      </div>
      <div className="text-sm text-ink">{children}</div>
    </div>
  );
}

function SupportsBanner({ supports }: { supports: JiraSupports }) {
  return (
    <section className="rounded-lg border border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50/60 dark:bg-brand-navy-500/10 p-4">
      <div className="text-[11px] font-medium uppercase tracking-wide text-brand-navy-700 dark:text-brand-navy-300">
        Supports
      </div>
      <div className="mt-0.5 text-sm font-semibold text-ink">
        <span className="font-mono text-xs text-ink-muted">
          {supports.project_id}
        </span>{" "}
        — {supports.project_name}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <Row label="Team">{supports.team ?? "—"}</Row>
        <Row label="Department">{supports.department ?? "—"}</Row>
        <Row label="Sponsor">{supports.sponsor ?? "—"}</Row>
        <Row label="Progress">
          {Math.round((supports.pct_complete ?? 0) * 100)}%
        </Row>
        <Row label="Health">
          <span className="inline-flex items-center gap-1.5">
            <span
              className={cn(
                "h-2 w-2 rounded-full",
                healthDot(supports.health),
              )}
            />
            {supports.health ?? "—"}
          </span>
        </Row>
        <Row label="Status">{supports.status ?? "—"}</Row>
        {(supports.start_date || supports.end_date) && (
          <Row label="Window">
            {formatDate(supports.start_date)} – {formatDate(supports.end_date)}
          </Row>
        )}
      </dl>
    </section>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt className="text-ink-muted">{label}</dt>
      <dd className="font-medium text-ink">{children}</dd>
    </>
  );
}
